/**
 * 外観設定(テーマの選択。2026-09-22 D-132)。システム設定の中に置く。
 *  - 4つのテーマをカードで並べ、小さな見本を出す。**「適用する」を押したときだけ保存**(カードを選んだだけでは変わらない)
 *  - 保存先はこのブラウザ・このアカウント(themes.ts)。ほかの人・ほかのブラウザには影響しない
 *  - デザイン比較(/design-preview)から ?theme= 付きで来たら、そのテーマを選んだ状態で開く
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@core/components/Toast';
import Panel from '@core/components/ui/Panel';
import StatusBadge from '@core/components/ui/StatusBadge';
import { useTheme } from './ThemeProvider';
import { THEMES, THEME_IDS, type ThemeId } from './themes';

/** テーマの小さな見本(色と枠の違いが分かる程度。値は themes.css の .theme-scope) */
function ThemeMini({ id }: { id: ThemeId }) {
  return (
    <div className="theme-mini theme-scope" data-theme={id} aria-hidden="true">
      <div className="theme-mini-side">
        <span className="theme-mini-brand" />
        <span className="theme-mini-nav is-on" />
        <span className="theme-mini-nav" />
        <span className="theme-mini-nav" />
      </div>
      <div className="theme-mini-main">
        <div className="theme-mini-top" />
        <div className="theme-mini-body">
          <span className="theme-mini-title" />
          <div className="theme-mini-kpis">
            <span className="theme-mini-kpi">
              <i style={{ width: '70%' }} />
            </span>
            <span className="theme-mini-kpi is-alert">
              <i style={{ width: '45%' }} />
            </span>
          </div>
          <div className="theme-mini-card">
            <span className="theme-mini-row is-head" />
            <span className="theme-mini-row" />
            <span className="theme-mini-row" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ThemeSettingsPanel({ initial, onDirtyChange }: { initial?: ThemeId | null; onDirtyChange?: (dirty: boolean) => void }) {
  const { officialTheme, applyTheme } = useTheme();
  const toast = useToast();
  const [selected, setSelected] = useState<ThemeId>(initial ?? officialTheme);
  const dirty = selected !== officialTheme;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const apply = () => {
    if (!applyTheme(selected)) {
      toast('保存できませんでした(ログインし直してからお試しください)');
      return;
    }
    toast(`テーマを「${THEMES[selected].label}」にしました`);
  };

  return (
    <Panel
      id="appearance"
      title="外観設定(テーマ)"
      meta={`いま: ${THEMES[officialTheme].label}`}
      note="選んだテーマは **このブラウザの、あなたのアカウントにだけ** 使われます(ほかの人・ほかのブラウザ・データには影響しません)。カードを選んで「適用する」を押すと切り替わります"
    >
      <div className="theme-cards" role="radiogroup" aria-label="テーマ">
        {THEME_IDS.map((id) => (
          <label key={id} className={`theme-card${selected === id ? ' is-selected' : ''}`}>
            <input type="radio" className="sr-only" name="theme" value={id} checked={selected === id} onChange={() => setSelected(id)} />
            <ThemeMini id={id} />
            <span className="theme-card-body">
              <span className="theme-card-title">
                {THEMES[id].label}
                {officialTheme === id && <StatusBadge kind="confirmed" label="使用中" />}
              </span>
              <span className="theme-card-desc">{THEMES[id].description}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="theme-actions">
        <Link className="ui-btn ui-btn-secondary" to={`/design-preview?theme=${selected}`}>
          実際の画面で比べる
        </Link>
        <span className="theme-actions-end">
          <button type="button" className="ui-btn ui-btn-secondary" disabled={!dirty} onClick={() => setSelected(officialTheme)}>
            キャンセル
          </button>
          <button type="button" className="ui-btn ui-btn-primary" disabled={!dirty} onClick={apply}>
            適用する
          </button>
        </span>
      </div>
    </Panel>
  );
}
